import { Grid, Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Chart, enqueueSnackbar, formatAmount, FormProvider, RHFAutocomplete, Spinner } from '@zeta-core';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';

export const InvestmentGoalTracker = ({ clientID, isClient }: { clientID?: string; isClient?: boolean }) => {
  const [goalDropDown, setGoalDropDown] = useState([]);
  const theme = useTheme();

  const methods = useForm({
    defaultValues: {
      goal: '',
    },
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const getInvestmentGoalTracker = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getInvestmentGoalTracker(payload) : FinancialMisService().getInvestmentGoalTrackerAdvisor(payload),
    onSuccess: (response) => {
      setGoalDropDown(response?.data);
      const goal = response?.data?.[0];
      methods.reset({
        goal,
      });
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    const payload = {
      clientID,
    };

    getInvestmentGoalTracker?.mutateAsync(payload);
  }, [clientID]); // Add currentFrequency as dependency

  const goalData = methods?.watch('goal') || {};

  // Transform data for series
  const series = [];

  // Add area chart for projections
  if (goalData?.projectionMap && Object.keys(goalData?.projectionMap).length > 0) {
    const projectionData = Object.entries(goalData?.projectionMap).map(([year, value]) => ({
      x: year,
      y: value,
    }));

    series.push({
      name: 'Projection',
      type: 'area',
      data: projectionData,
    });
  }

  // Add bar chart for actual growth
  if (goalData?.actualGrowthMap && Object.keys(goalData?.actualGrowthMap).length > 0) {
    const actualData = Object.entries(goalData?.actualGrowthMap).map(([year, value]) => ({
      x: year,
      y: value,
    }));

    series.push({
      name: 'Current',
      type: 'column',
      data: actualData,
    });
  }

  const getAxisRanges = (goalData) => {
    const targetAmount = goalData?.targetAmount || 0;
    const projectionValues = Object.values(goalData?.projectionMap || {}).map(Number);
    const actualValues = Object.values(goalData?.actualGrowthMap || {}).map(Number);

    const allYValues = [...projectionValues, ...actualValues, targetAmount].filter(Boolean);
    const maxY = Math.max(...allYValues);

    return {
      maxAmount: maxY < targetAmount ? targetAmount * 1.2 : maxY * 1.3,
    };
  };

  const { maxAmount } = getAxisRanges(goalData);

  const chartOptions = {
    plotOptions: {
      bar: {
        columnWidth: '5%',
        borderRadius: 4,
      },
      line: {
        width: '20px',
      },
    },
    stroke: {
      width: [2, 1],
      curve: 'straight',
    },
    fill: {
      opacity: [0.3, 1],
      type: ['solid', 'solid'],
    },
    colors: [theme.palette.primary.main, theme.palette.warning.main],
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (value) => `£${(value / 1000).toFixed(1)}k`,
      },
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'right',
      paddingRight: 4,
    },
    annotations: {
      xaxis: [
        {
          x: goalData?.targetYear?.toString(),
          strokeDashArray: 7,
          borderColor: theme.palette.primary.main,
          label: {
            offsetX: -70,
            borderColor: theme.palette.primary.main,
            orientation: 'horizontal',
            text: `${goalData?.goalName} : ${formatAmount(goalData?.targetAmount)}`,
            style: {
              color: theme.palette.primary.contrastText,
              background: theme.palette.primary.main,
              borderRadius: 8,
              fontFamily: theme.typography.fontFamily,
              fontSize: '13px',
              padding: {
                top: 4,
                bottom: 6,
                right: 8,
                left: 8,
              },
            },
          },
        },
      ],
    },
    chart: {
      height: '100%',
      type: 'bar',
      toolbar: {
        show: false,
      },
      zoom: {
        enabled: false,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      type: 'datetime',
      tickAmount: 15, // Controls the number of ticks on the x-axis
      labels: {
        // show: false,
        rotate: -45, // Rotate labels for better readability
        formatter: (value) => {
          const date = new Date(value);
          return `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`; // "Jun 2026"
        },
      },
    },
    yaxis: {
      min: 0,
      max: maxAmount,
      tickAmount: 6, // Control number of y-axis ticks
      labels: {
        formatter: (value) => {
          if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
          if (value >= 1000) return `£${(value / 1000).toFixed(0)}k`; // Removed decimal places
          return `£${value}`;
        },
        style: {
          colors: theme.palette.text.secondary,
        },
      },
    },
  };

  return (
    <>
      <Stack px={2} sx={{ mt: -7 }}>
        <FormProvider methods={methods}>
          <Grid container>
            <Grid
              size={{
                lg: 3
              }}>
              <RHFAutocomplete
                size="small"
                name="goal"
                options={goalDropDown || []}
                placeholder="Select Goal"
                label="Select Goal"
                loading={getInvestmentGoalTracker?.isPending}
                getOptionLabel={(option) => option?.goalName}
                isOptionEqualToValue={(option, value) => option?.goalId === value?.goalId}
                disableClearable
              />
            </Grid>
          </Grid>
        </FormProvider>
      </Stack>
      {getInvestmentGoalTracker?.isPending ? (
        <Stack alignItems="center" justifyContent="center" minHeight="30vh">
          <Spinner />
        </Stack>
      ) : (
        <Stack justifyContent="center" alignItems="center" minHeight="30vh">
          {goalDropDown?.length > 0 ? (
            <Chart dir="ltr" type="area" series={series} options={chartOptions} height={320} style={{ paddingRight: '16px', width: '100%' }} />
          ) : (
            <Typography variant="subtitle2">No Data</Typography>
          )}
        </Stack>
      )}
    </>
  );
};
